import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function MentionsLegales() {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com';
    const scriptId = 'jsonld-mentions-legales';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mentions Légales | Usine à Leads',
      description: "Mentions légales d'Usine à Leads : éditeur, hébergeur, propriété intellectuelle, responsabilité et données personnelles.",
      url: `${siteUrl}/mentions-legales`,
      inLanguage: 'fr-FR',
      publisher: {
        '@type': 'Organization',
        name: 'Usine à Leads',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/62fb907743b5e07417bde9cd3a46f134.png',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Mentions légales', item: `${siteUrl}/mentions-legales` },
        ],
      },
    });
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  // Helper to format the current date in French locale
  const formattedDate = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // Current year for the footer
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-white">
      <title>Mentions Légales | Usine à Leads</title>
      <meta name="description" content="Consultez les mentions légales d'Usine à Leads : éditeur du site, hébergeur, propriété intellectuelle, limitation de responsabilité et données personnelles." />
      <meta name="keywords" content="mentions légales usine à leads, éditeur site, propriété intellectuelle" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/mentions-legales`} />
      <meta property="og:title" content="Mentions Légales | Usine à Leads" />
      <meta property="og:description" content="Mentions légales d'Usine à Leads : éditeur, hébergeur, propriété intellectuelle et données personnelles." />
      <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/mentions-legales`} />
      <meta property="og:type" content="website" />

      {/* Navbar */}
      <nav className="w-full bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/8eef813465f798e5a4134d50e400907e.png"
              alt="Usine à Leads"
              className="h-10 w-auto"
              loading="eager"
              decoding="async"
              width="140"
              height="40"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Retour à l&apos;accueil
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          Mentions légales
        </h1>
        <p className="text-sm text-gray-400 mb-10">
          Dernière mise à jour : {formattedDate}
        </p>
        <hr className="border-gray-100 mb-10" />

        <article className="prose-custom space-y-10 text-gray-700 text-[15px] leading-relaxed">
          {/* Éditeur */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              1. Éditeur du site
            </h2>
            <p>
              Le site <strong>usineleads.com</strong> (ci-après « le Site ») est
              édité par :
            </p>
            <div className="bg-gray-50 rounded-xl p-5 mt-4 border-l-4 border-orange-400 space-y-1 text-sm">
              <p>
                <strong>Raison sociale :</strong> Usine à Leads
              </p>
              <p>
                <strong>Forme juridique :</strong>{' '}
                [À compléter – ex. SAS, SARL, auto-entrepreneur]
              </p>
              <p>
                <strong>Capital social :</strong> [À compléter]
              </p>
              <p>
                <strong>Siège social :</strong> Paris, France
              </p>
              <p>
                <strong>SIRET :</strong> [À compléter]
              </p>
              <p>
                <strong>RCS :</strong> [À compléter]
              </p>
              <p>
                <strong>Numéro de TVA intracommunautaire :</strong>{' '}
                [À compléter]
              </p>
              <p>
                <strong>Directeur de la publication :</strong>{' '}
                [À compléter]
              </p>
              <p>
                <strong>Email :</strong> contact@usineleads.com
              </p>
              <p>
                <strong>Téléphone :</strong> +33 1 23 45 67 89
              </p>
            </div>
          </section>

          {/* Hébergeur */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              2. Hébergeur
            </h2>
            <p>Le Site est hébergé par :</p>
            <div className="bg-gray-50 rounded-xl p-5 mt-4 border-l-4 border-orange-400 space-y-1 text-sm">
              <p>
                <strong>Nom :</strong> [À compléter – ex. OVH, Vercel, Netlify]
              </p>
              <p>
                <strong>Adresse :</strong> [À compléter]
              </p>
              <p>
                <strong>Téléphone :</strong> [À compléter]
              </p>
            </div>
          </section>

          {/* Activité */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              3. Activité
            </h2>
            <p>
              Usine à Leads est une agence spécialisée dans la{' '}
              <strong>génération de leads qualifiés</strong>, la mise en
              place de stratégies d&apos;acquisition digitale, la création de
              tunnels de conversion et l&apos;automatisation marketing. Les
              prestations fournies sont des{' '}
              <strong>
                prestations de services logicielles et média
              </strong>
              , soumises à une obligation de moyens et non de résultats.
            </p>
          </section>

          {/* Propriété intellectuelle */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              4. Propriété intellectuelle
            </h2>
            <p>
              L&apos;ensemble des contenus présents sur le Site (textes,
              images, graphismes, logos, icônes, vidéos, logiciels, bases de
              données, etc.) est protégé par les lois françaises et
              internationales relatives à la propriété intellectuelle.
            </p>
            <p className="mt-3">
              Toute reproduction, représentation, modification, publication,
              adaptation, totale ou partielle, de ces éléments, quel que soit le
              moyen ou le procédé utilisé, est interdite sans l&apos;autorisation
              écrite préalable d&apos;Usine à Leads.
            </p>
          </section>

          {/* Responsabilité */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              5. Limitation de responsabilité
            </h2>
            <p>
              Usine à Leads s&apos;efforce d&apos;assurer l&apos;exactitude et la
              mise à jour des informations diffusées sur le Site. Toutefois,
              Usine à Leads ne peut garantir l&apos;exactitude, la précision ou
              l&apos;exhaustivité des informations mises à disposition.
            </p>
            <p className="mt-3">
              En conséquence, Usine à Leads décline toute responsabilité pour
              toute imprécision, inexactitude ou omission portant sur des
              informations disponibles sur le Site, ainsi que pour tout dommage
              résultant d&apos;une intrusion frauduleuse d&apos;un tiers.
            </p>
          </section>

          {/* Liens hypertextes */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              6. Liens hypertextes
            </h2>
            <p>
              Le Site peut contenir des liens hypertextes vers d&apos;autres
              sites internet. Usine à Leads n&apos;exerce aucun contrôle sur ces
              sites et décline toute responsabilité quant à leur contenu ou aux
              pratiques de confidentialité de ces tiers.
            </p>
          </section>

          {/* Données personnelles */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              7. Données personnelles
            </h2>
            <p>
              Les informations relatives au traitement des données personnelles
              sont détaillées dans notre{' '}
              <Link
                to="/politique-de-confidentialite"
                className="text-orange-500 hover:underline cursor-pointer"
              >
                Politique de confidentialité
              </Link>
              .
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Cookies</h2>
            <p>
              Le Site utilise des cookies. Pour en savoir plus sur l&apos;utilisation
              des cookies et la gestion de vos préférences, veuillez consulter
              notre{' '}
              <Link
                to="/politique-de-confidentialite"
                className="text-orange-500 hover:underline cursor-pointer"
              >
                Politique de confidentialité
              </Link>
              .
            </p>
          </section>

          {/* Droit applicable */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              9. Droit applicable et juridiction
            </h2>
            <p>
              Les présentes mentions légales sont régies par le droit français.
              En cas de litige, et après tentative de résolution amiable, les
              tribunaux compétents de Paris seront seuls compétents.
            </p>
          </section>

          {/* Médiation */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Médiation</h2>
            <p>
              Conformément aux articles L.616-1 et R.616-1 du Code de la
              consommation, le client consommateur peut recourir gratuitement au
              service de médiation proposé par Usine à Leads. Le médiateur
              « droit de la consommation » ainsi proposé est : [À compléter].
            </p>
          </section>
        </article>
      </main>

      {/* Footer minimal */}
      <footer className="border-t border-gray-100 py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-400">
          <p>© {currentYear} Usine à Leads. Tous droits réservés.</p>
          <nav className="flex gap-6">
            <Link
              to="/mentions-legales"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap font-medium text-gray-600"
            >
              Mentions légales
            </Link>
            <Link
              to="/politique-de-confidentialite"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Politique de confidentialité
            </Link>
            <Link
              to="/cgv"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              CGV
            </Link>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
